/**
 * The media library as the interface sees it: what a file is, whether the
 * gateway will take it, and how a field value (`/media/2026/copertina.webp`,
 * or the full address a migrated page carries) maps back to an asset path.
 *
 * The limits repeat the gateway's (`MAX_ASSET_BYTES`, `ALLOWED_MEDIA_TYPES`
 * in apps/gateway/src/media/inspect.ts) on purpose: checking here saves a
 * 10 MB upload that would be refused at the last byte. The gateway still
 * decides — it reads the type from the bytes, not from the name.
 */

export type MediaKind = 'image' | 'document' | 'video'
export const MEDIA_KINDS: readonly MediaKind[] = ['image', 'document', 'video']

export type MediaStatusFilter = 'active' | 'archived' | 'all'
export const MEDIA_STATUS_FILTERS: readonly MediaStatusFilter[] = ['active', 'archived', 'all']

/** 10 MB, as `MAX_ASSET_BYTES` in packages/domain/src/aggregates/asset.ts. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

const EXTENSIONS: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  mp4: 'video/mp4',
}

export const ACCEPTED_TYPES: readonly string[] = [...new Set(Object.values(EXTENSIONS))]

/** For `<input type="file" accept>`: types and extensions, because some systems give files no type. */
export const ACCEPT_ATTRIBUTE = [...ACCEPTED_TYPES, ...Object.keys(EXTENSIONS).map((extension) => `.${extension}`)].join(',')

export function mediaKindOf(mimeType: string | null | undefined): MediaKind | null {
  if (!mimeType) return null
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('application/')) return 'document'
  return null
}

export function isMediaKind(value: string | null | undefined): value is MediaKind {
  return !!value && (MEDIA_KINDS as readonly string[]).includes(value)
}

export function isMediaStatusFilter(value: string | null | undefined): value is MediaStatusFilter {
  return !!value && (MEDIA_STATUS_FILTERS as readonly string[]).includes(value)
}

export type UploadProblem = 'empty' | 'tooLarge' | 'unsupported'

/**
 * Why a file would be refused, or `null`. The type comes from the browser
 * when it knows it, from the extension otherwise; an unknown type with a
 * known extension goes through and the gateway reads the bytes.
 */
export function uploadProblem(file: { name: string; size: number; type: string }): UploadProblem | null {
  if (file.size === 0) return 'empty'
  if (file.size > MAX_UPLOAD_BYTES) return 'tooLarge'
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  const declared = file.type.toLowerCase()
  if (declared && ACCEPTED_TYPES.includes(declared)) return null
  if (!declared && extension in EXTENSIONS) return null
  // A browser that calls a .jpg "image/pjpeg" or similar: trust the extension, the bytes decide.
  if (declared.startsWith('image/') && extension in EXTENSIONS) return null
  return 'unsupported'
}

/** "1,2 MB", "830 kB", "512 B" — decimal units, as file managers show them. */
export function formatBytes(bytes: number | null | undefined, locale: string): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1000) return `${bytes} B`
  const units = ['kB', 'MB', 'GB']
  let value = bytes / 1000
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000
    unit += 1
  }
  const digits = value < 10 ? 1 : 0
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value)} ${units[unit]}`
}

/** The alt text in the interface language, else Italian, else the first one written. */
export function altFor(alt: Record<string, string> | null | undefined, locale: string): string {
  if (!alt) return ''
  return alt[locale] ?? alt.it ?? Object.values(alt)[0] ?? ''
}

/**
 * The library path (`media/2026/x.webp`) a field value points at, or `null`
 * when it cannot be one: a field holds `/media/…`, and a page migrated from
 * the old site may hold the full `https://dominio/media/…` address.
 */
export function libraryPathFromValue(value: string | null | undefined): string | null {
  const raw = value?.trim()
  if (!raw) return null
  let path = raw
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname
    } catch {
      return null
    }
  }
  path = path.split(/[?#]/)[0] ?? ''
  if (!path.startsWith('/')) path = `/${path}`
  try {
    path = decodeURI(path)
  } catch {
    // A malformed escape is left as written: the lookup simply finds nothing.
  }
  return /^\/media\/[^/]/.test(path) ? path.slice(1) : null
}

/** The name the library shows: the asset's name, else the last segment of its path. */
export function mediaDisplayName(asset: { name: string; path: string }): string {
  return asset.name.trim() || asset.path.split('/').pop() || asset.path
}
